import React from 'react';
import YearTable from './YearTable';
import RequirementsPanel from './RequirementsPanel';
import {
  AcademicSystem,
  CourseEntry,
  CourseFieldKey,
  MajorRequirement,
  MinorRequirement,
  MajorSummary,
  MinorSummary,
  MultiSelectOption,
  Requirement,
  RequirementSummary,
  YearPlan,
} from '../../types';

type PlannerViewProps = {
  selection: AcademicSystem;
  yearPlans: YearPlan[];
  requirements: Requirement[];
  majors: MajorRequirement[];
  minors: MinorRequirement[];
  showDistributives: boolean;
  distributiveOptions: MultiSelectOption[];
  requirementSummaries: RequirementSummary[];
  majorSummaries: MajorSummary[];
  minorSummaries: MinorSummary[];
  hasClassRequirement: boolean;
  totalClassesRemaining: number | null;
  totalClassRequirement: number;
  onAddYear: () => void;
  onRemoveYear: (yearId: string) => void;
  onGoBack: () => void;
  onResetPlan: () => void;
  onAddCourse: (yearId: string, term: string) => void;
  onRemoveCourse: (yearId: string, term: string, courseId: string) => void;
  onUpdateCourse: (
    yearId: string,
    term: string,
    courseId: string,
    field: CourseFieldKey,
    value: CourseEntry[CourseFieldKey]
  ) => void;
  onUpdateTermCompletion: (yearId: string, term: string, completed: boolean) => void;
  onToggleDistributives: (value: boolean) => void;
  onAddRequirement: () => void;
  onUpdateRequirement: (id: string, field: 'name' | 'count', value: string) => void;
  onRemoveRequirement: (id: string) => void;
  onAddMajor: () => void;
  onUpdateMajorName: (id: string, value: string) => void;
  onUpdateMajorClass: (majorId: string, classId: string, value: string) => void;
  onAddMajorClass: (majorId: string) => void;
  onRemoveMajor: (majorId: string) => void;
  onRemoveMajorClass: (majorId: string, classId: string) => void;
  onAddMinor: () => void;
  onUpdateMinorName: (id: string, value: string) => void;
  onUpdateMinorClass: (minorId: string, classId: string, value: string) => void;
  onAddMinorClass: (minorId: string) => void;
  onRemoveMinor: (minorId: string) => void;
  onRemoveMinorClass: (minorId: string, classId: string) => void;
};

const courseHasContent = (course: CourseEntry) =>
  Boolean(
    course.name.trim() !== '' ||
      course.distributives.length > 0 ||
      course.requiredCourseId ||
      course.isMajorRequirement
  );

const countPlannedCourses = (plans: YearPlan[]) =>
  plans.reduce(
    (yearTotal, year) =>
      yearTotal +
      year.terms.reduce(
        (termTotal, term) => termTotal + term.courses.filter(courseHasContent).length,
        0
      ),
    0
  );

const PlannerView: React.FC<PlannerViewProps> = ({
  selection,
  yearPlans,
  requirements,
  majors,
  minors,
  showDistributives,
  distributiveOptions,
  requirementSummaries,
  majorSummaries,
  minorSummaries,
  hasClassRequirement,
  totalClassesRemaining,
  totalClassRequirement,
  onAddYear,
  onRemoveYear,
  onGoBack,
  onResetPlan,
  onAddCourse,
  onRemoveCourse,
  onUpdateCourse,
  onUpdateTermCompletion,
  onToggleDistributives,
  onAddRequirement,
  onUpdateRequirement,
  onRemoveRequirement,
  onAddMajor,
  onUpdateMajorName,
  onUpdateMajorClass,
  onAddMajorClass,
  onRemoveMajor,
  onRemoveMajorClass,
  onAddMinor,
  onUpdateMinorName,
  onUpdateMinorClass,
  onAddMinorClass,
  onRemoveMinor,
  onRemoveMinorClass,
}) => {
  const plannedCourseCount = countPlannedCourses(yearPlans);
  const unmetDistributives = requirementSummaries.filter((item) => item.remaining > 0);
  const unmetMajors = majorSummaries.filter((item) => item.remaining > 0);
  const unmetMinors = minorSummaries.filter((item) => item.remaining > 0);
  const classGap = hasClassRequirement ? Math.max(totalClassRequirement - plannedCourseCount, 0) : 0;
  const hasTrackableInputs =
    hasClassRequirement ||
    requirementSummaries.length > 0 ||
    majorSummaries.length > 0 ||
    minorSummaries.length > 0;
  const onTrack =
    hasTrackableInputs &&
    classGap === 0 &&
    unmetDistributives.length === 0 &&
    unmetMajors.length === 0 &&
    unmetMinors.length === 0;

  const remainingParts: string[] = [];
  if (classGap > 0) {
    remainingParts.push(`${classGap} more class${classGap === 1 ? '' : 'es'}`);
  }
  const distributiveGap = unmetDistributives.reduce((sum, item) => sum + item.remaining, 0);
  if (distributiveGap > 0) {
    remainingParts.push(`${distributiveGap} distributive${distributiveGap === 1 ? '' : 's'}`);
  }
  const majorGap = unmetMajors.reduce((sum, item) => sum + item.remaining, 0);
  if (majorGap > 0) {
    remainingParts.push(`${majorGap} major requirement${majorGap === 1 ? '' : 's'}`);
  }
  const minorGap = unmetMinors.reduce((sum, item) => sum + item.remaining, 0);
  if (minorGap > 0) {
    remainingParts.push(`${minorGap} minor requirement${minorGap === 1 ? '' : 's'}`);
  }

  const statusText = !hasTrackableInputs
    ? 'Add your graduation requirements to check if your plan is on track.'
    : onTrack
    ? 'You’re on track to graduate with the courses in this plan.'
    : `Not on track yet — ${remainingParts.join(', ') || 'add the missing details.'}`;

  const statusTone = onTrack
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : hasTrackableInputs
    ? 'border-amber-200 bg-amber-50 text-amber-800'
    : 'border-slate-200 bg-slate-50 text-slate-700';
  const statusDot = onTrack ? 'bg-emerald-500' : hasTrackableInputs ? 'bg-amber-500' : 'bg-slate-400';

  return (
    <section className="mt-12 space-y-6 -mx-6 md:-mx-12 lg:-mx-16 px-6 md:px-12 lg:px-16">
      <div className="space-y-4 text-left">
        <div className="space-y-2 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Planner</p>
          <h2 className="text-2xl font-serif font-semibold text-slate-900">Yearly plan at a glance</h2>
          <p className="text-sm text-slate-600">
            List classes per term, mark major requirements, and track completion one term at a time.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={onGoBack}
            className="rounded-full border border-slate-300 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700 transition duration-200 hover:-translate-y-0.5 hover:border-slate-900 hover:text-slate-900 hover:shadow-[0_14px_40px_-28px_rgba(15,23,42,0.55)]"
          >
            Back to setup
          </button>
          <button
            type="button"
            onClick={() => {
              const confirmed = window.confirm('Add another year to your plan?');
              if (confirmed) {
                onAddYear();
              }
            }}
            className="rounded-full border border-slate-900 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-900 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-28px_rgba(15,23,42,0.55)]"
          >
            Add another year
          </button>
          <button
            type="button"
            onClick={onResetPlan}
            className="rounded-full border border-rose-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-rose-700 transition duration-200 hover:-translate-y-0.5 hover:border-rose-600 hover:bg-rose-50 hover:text-rose-800 hover:shadow-[0_14px_40px_-26px_rgba(244,63,94,0.55)]"
          >
            Reset plan
          </button>
          <label className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <input
              type="checkbox"
              checked={showDistributives}
              onChange={(event) => onToggleDistributives(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            />
            Show distributives
          </label>
        </div>
        <div className={`flex items-start gap-3 rounded-2xl border p-4 shadow-[0_10px_40px_-36px_rgba(15,23,42,0.55)] ${statusTone}`}>
          <span className={`mt-1 inline-flex h-3 w-3 rounded-full ${statusDot}`} aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em]">Graduation readiness</p>
            <p className="text-sm leading-relaxed">{statusText}</p>
          </div>
        </div>
      </div>

      <YearTable
        selection={selection}
        yearPlans={yearPlans}
        majors={majors}
        minors={minors}
        showDistributives={showDistributives}
        distributiveOptions={distributiveOptions}
        onRemoveYear={onRemoveYear}
        onAddCourse={onAddCourse}
        onRemoveCourse={onRemoveCourse}
        onUpdateCourse={onUpdateCourse}
        onUpdateTermCompletion={onUpdateTermCompletion}
      />

      <RequirementsPanel
        requirements={requirements}
        majors={majors}
        minors={minors}
        requirementSummaries={requirementSummaries}
        majorSummaries={majorSummaries}
        minorSummaries={minorSummaries}
        hasClassRequirement={hasClassRequirement}
        totalClassesRemaining={totalClassesRemaining}
        totalClassRequirement={totalClassRequirement}
        onAddRequirement={onAddRequirement}
        onUpdateRequirement={onUpdateRequirement}
        onRemoveRequirement={onRemoveRequirement}
        onAddMajor={onAddMajor}
        onUpdateMajorName={onUpdateMajorName}
        onUpdateMajorClass={onUpdateMajorClass}
        onAddMajorClass={onAddMajorClass}
        onRemoveMajor={onRemoveMajor}
        onRemoveMajorClass={onRemoveMajorClass}
        onAddMinor={onAddMinor}
        onUpdateMinorName={onUpdateMinorName}
        onUpdateMinorClass={onUpdateMinorClass}
        onAddMinorClass={onAddMinorClass}
        onRemoveMinor={onRemoveMinor}
        onRemoveMinorClass={onRemoveMinorClass}
      />
    </section>
  );
};

export default PlannerView;
